<template>
  <v-container>
    <h3 style="padding-bottom: 20px;">设置</h3>
    <v-row>
      <v-col cols="12">
        <v-tabs v-model="settingsTab" color="primary" show-arrows>
          <v-tab v-for="tab in settingTabs" :key="tab.value" :href="`#${tab.value}`">
            {{ tab.text }}
          </v-tab>
        </v-tabs>
      </v-col>
    </v-row>

    <v-tabs-items v-model="settingsTab" style="background: transparent;">
      <v-tab-item value="security">
        <v-row>
          <v-col cols="12">
            <div class="v-card__title px-0">账户登录和安全</div>
            <v-btn @click="logout">退出登录</v-btn>
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12">
            <v-container style="padding: 0px;" v-if="f2a_loading">
              <v-progress-circular indeterminate color="primary"></v-progress-circular>
              <span>TOPT设置加载中...</span>
            </v-container>
            <v-container style="padding: 0px;" v-else>
              <v-row>
                <v-col cols="12" md="6">
                  <v-card class="pa-4" elevation="3">
                    <v-card-title class="text-h6 font-weight-bold">
                      双因素认证 (2FA)
                    </v-card-title>
                    <div v-if="f2a_isBound" class="text-center">
                      <v-alert type="success">
                        账户已绑定双因素认证。
                      </v-alert>
                    </div>
                    <div v-else>
                      <v-card-text>
                        <div class="mb-4">
                          使用 <strong>Google Authenticator</strong> 或 <strong>Authy</strong> 等应用扫描下方二维码：
                        </div>
                        <div class="d-flex justify-center">
                          <v-img :src="f2a_qrCodeUrl" max-width="200" max-height="200" contain></v-img>
                        </div>
                        <div class="mt-3 text-center">
                          或手动输入密钥：<br>
                          <strong class="text-body-2">{{ f2a_secretKey }}</strong>
                        </div>
                        <v-text-field v-model="f2a_otpCode" label="输入验证码" placeholder="6位数字" maxlength="6"
                          class="mt-4"></v-text-field>
                        <span v-if="f2a_tips" style="color: red; font-weight: bold;">{{ f2a_tips }}</span>
                      </v-card-text>
                      <v-card-actions>
                        <v-spacer></v-spacer>
                        <v-btn color="primary" @click="verifyCode">验证</v-btn>
                      </v-card-actions>
                    </div>
                  </v-card>
                </v-col>
              </v-row>
            </v-container>
          </v-col>
        </v-row>
      </v-tab-item>

      <v-tab-item v-if="isDomainUser" value="register">
        <v-row>
          <v-col cols="12">
            <v-card>
              <v-card-title>
                域名账号注册
              </v-card-title>
              <v-card-text>
                <div style="margin-bottom: 8px;">当前域名：<strong>{{ logininfo.domain_name }}</strong></div>
                <v-alert text dense type="info">
                  域名主账号现在采用系统管理员邀请码注册，不再开放公开自助注册。
                </v-alert>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
        <v-row v-if="!canManageDomainAccounts">
          <v-col cols="12">
            <v-alert type="info" outlined>
              当前为子账号，同域账号请由主账号管理。
            </v-alert>
          </v-col>
        </v-row>
      </v-tab-item>

      <v-tab-item v-if="canManageDomainAccounts" value="members">
        <v-row>
          <v-col cols="12">
            <v-card>
              <v-card-title>
                邮箱成员管理
              </v-card-title>
              <v-card-text>
                <v-alert text dense type="info">
                  登录账号和邮箱资源是分开的。主账号可以创建普通账号、维护邮箱资源，并把某个邮箱资源授权给普通账号使用。
                </v-alert>

                <div class="text-subtitle-1 font-weight-bold" style="margin: 20px 0 12px;">同域账号</div>
                <v-row>
                  <v-col cols="12" md="4">
                    <v-text-field
                      v-model="domainAccountForm.local_part"
                      :disabled="domainAccountForm.id !== null"
                      label="邮箱名"
                      placeholder="user"
                      :suffix="`@${logininfo.domain_name || 'example.com'}`"
                    ></v-text-field>
                  </v-col>
                  <v-col cols="12" md="4">
                    <v-text-field
                      v-model="domainAccountForm.tag"
                      label="显示名称"
                      placeholder="可选"
                    ></v-text-field>
                  </v-col>
                  <v-col cols="12" md="4">
                    <v-text-field
                      v-model="domainAccountForm.password"
                      :label="domainAccountForm.id ? '新密码' : '登录密码'"
                      type="password"
                      :placeholder="domainAccountForm.id ? '留空则不修改' : '请输入密码'"
                    ></v-text-field>
                  </v-col>
                </v-row>
                <v-row>
                  <v-col cols="12">
                    <v-btn color="primary" @click="saveDomainAccount">
                      {{ domainAccountForm.id ? '保存账号' : '新增账号' }}
                    </v-btn>
                    <v-btn style="margin-left: 12px;" @click="resetDomainAccountForm">清空表单</v-btn>
                    <v-btn style="margin-left: 12px;" @click="loadDomainAccounts">刷新列表</v-btn>
                  </v-col>
                </v-row>
                <v-row v-if="domainAccountTips">
                  <v-col cols="12">
                    <v-alert text dense :type="domainAccountTipsType" v-show="true">{{ domainAccountTips }}</v-alert>
                  </v-col>
                </v-row>
                <v-data-table
                  :headers="domainAccountHeaders"
                  :items="domainAccounts"
                  :loading="domainAccountLoading"
                  class="mt-4"
                >
                  <template v-slot:[`item.account_type`]="{ item }">
                    <v-chip :color="item.is_domain_admin ? 'primary' : 'grey'" dark small>
                      {{ item.is_domain_admin ? '主账号' : '子账号' }}
                    </v-chip>
                  </template>
                  <template v-slot:[`item.granted_mailboxes`]="{ item }">
                    <span>{{ (item.granted_mailboxes || []).join(', ') || '-' }}</span>
                  </template>
                  <template v-slot:[`item.actions`]="{ item }">
                    <v-chip color="primary" dark @click="editDomainAccount(item)">
                      编辑
                    </v-chip>
                    <v-chip
                      v-if="!item.is_domain_admin"
                      color="success"
                      dark
                      style="margin-left: 8px;"
                      @click="manageDomainAccountMailboxes(item)"
                    >
                      管理邮箱
                    </v-chip>
                    <v-chip
                      v-if="!item.is_domain_admin"
                      color="warning"
                      dark
                      style="margin-left: 8px;"
                      @click="promoteDomainAccount(item)"
                    >
                      升级主账号
                    </v-chip>
                    <v-chip
                      v-if="!item.is_domain_admin"
                      color="secondary"
                      dark
                      style="margin-left: 8px;"
                      @click="deleteDomainAccount(item)"
                    >
                      删除
                    </v-chip>
                  </template>
                </v-data-table>

                <v-divider class="my-6"></v-divider>
                <div class="text-subtitle-1 font-weight-bold" style="margin-bottom: 12px;">邮箱资源</div>
                <v-row>
                  <v-col cols="12" md="6">
                    <v-text-field
                      v-model="groupMailboxForm.local_part"
                      label="邮箱名"
                      placeholder="sales"
                      :suffix="`@${logininfo.domain_name || 'example.com'}`"
                      hint="只需填写 @ 前面的部分"
                      persistent-hint
                    ></v-text-field>
                  </v-col>
                  <v-col cols="12" md="6">
                    <v-btn color="primary" @click="saveGroupMailbox">添加邮箱</v-btn>
                    <v-btn style="margin-left: 12px;" @click="loadGroupMailboxes">刷新列表</v-btn>
                  </v-col>
                </v-row>
                <v-row v-if="groupMailboxTips">
                  <v-col cols="12">
                    <v-alert text dense :type="groupMailboxTipsType" v-show="true">{{ groupMailboxTips }}</v-alert>
                  </v-col>
                </v-row>
                <v-data-table
                  :headers="groupMailboxHeaders"
                  :items="groupMailboxes"
                  :loading="groupMailboxLoading"
                  class="mt-4"
                >
                  <template v-slot:[`item.actions`]="{ item }">
                    <v-chip color="secondary" dark @click="deleteGroupMailbox(item)">
                      删除
                    </v-chip>
                  </template>
                </v-data-table>

                <v-divider class="my-6"></v-divider>
                <div class="text-subtitle-1 font-weight-bold" style="margin-bottom: 12px;">给账号分配邮箱</div>
                <v-row>
                  <v-col cols="12" md="6">
                    <v-select
                      v-model="selectedGrantAccountId"
                      :items="grantableAccounts"
                      item-text="uname"
                      item-value="id"
                      label="选择普通账号"
                      placeholder="请选择一个子账号"
                    ></v-select>
                  </v-col>
                  <v-col cols="12" md="6" v-if="selectedGrantAccount">
                    <v-alert text dense type="info" style="margin-top: 10px;">
                      当前账号可访问：{{ (selectedGrantAccount.accessible_mailboxes || []).join(', ') || selectedGrantAccount.uname }}
                    </v-alert>
                  </v-col>
                </v-row>
                <v-row v-if="selectedGrantAccount">
                  <v-col cols="12">
                    <div class="text-body-2" style="margin-bottom: 8px;">已分配的额外邮箱</div>
                    <div v-if="selectedGrantAccountMailboxes.length">
                      <v-chip
                        v-for="mailbox in selectedGrantAccountMailboxes"
                        :key="`${selectedGrantAccount.id}-${mailbox}`"
                        close
                        color="blue lighten-4"
                        style="margin-right: 8px; margin-bottom: 8px;"
                        @click:close="deleteMailboxGrant(mailbox)"
                      >
                        {{ mailbox }}
                      </v-chip>
                    </div>
                    <v-alert v-else text dense type="info">
                      当前账号还没有额外邮箱授权。
                    </v-alert>
                  </v-col>
                </v-row>
                <v-row v-if="selectedGrantAccount">
                  <v-col cols="12" md="6">
                    <v-select
                      v-model="mailboxGrantForm.mailbox"
                      :items="grantMailboxOptions"
                      item-text="mailbox"
                      item-value="mailbox"
                      label="新增一个邮箱授权"
                      placeholder="请选择邮箱资源"
                    ></v-select>
                  </v-col>
                  <v-col cols="12" md="6">
                    <v-btn color="primary" @click="saveMailboxGrant">添加授权</v-btn>
                  </v-col>
                </v-row>
                <v-row v-if="mailboxGrantTips">
                  <v-col cols="12">
                    <v-alert text dense :type="mailboxGrantTipsType" v-show="true">{{ mailboxGrantTips }}</v-alert>
                  </v-col>
                </v-row>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </v-tab-item>

      <v-tab-item v-if="isAdmin" value="channels">
        <v-row>
          <v-col cols="12">
            <v-card>
              <v-card-title>
                发信渠道管理
              </v-card-title>
              <v-card-text>
                <v-row>
                  <v-col cols="12" md="4">
                    <v-text-field v-model="senderChannelForm.domain" label="发信域名" placeholder="example.com"></v-text-field>
                  </v-col>
                  <v-col cols="12" md="4">
                    <v-select
                      v-model="senderChannelForm.type"
                      :items="senderChannelTypeItems"
                      item-text="text"
                      item-value="value"
                      label="渠道类型"
                    ></v-select>
                  </v-col>
                  <v-col cols="12" md="4">
                    <v-text-field
                      v-if="senderChannelForm.type === 'forward'"
                      v-model="senderChannelForm.forward_domain"
                      label="转发目标域名"
                      placeholder="target-domain.com"
                    ></v-text-field>
                    <v-text-field
                      v-else
                      v-model="senderChannelForm.ip"
                      label="发信节点 IP"
                      placeholder="127.0.0.1"
                    ></v-text-field>
                  </v-col>
                </v-row>
                <v-row v-if="senderChannelForm.type === 'forward'">
                  <v-col cols="12">
                    <v-alert text dense type="info">
                      forward 表示当前域名不直接发信，而是复用目标域名的 agent 路由。
                    </v-alert>
                  </v-col>
                </v-row>
                <v-row>
                  <v-col cols="12" md="8">
                    <v-btn color="primary" @click="saveSenderChannel">
                      {{ senderChannelForm.id ? '保存修改' : '新增渠道' }}
                    </v-btn>
                    <v-btn style="margin-left: 12px;" @click="resetSenderChannelForm">清空表单</v-btn>
                    <v-btn style="margin-left: 12px;" @click="loadSenderChannels">刷新列表</v-btn>
                  </v-col>
                </v-row>
                <v-row v-if="senderChannelTips">
                  <v-col cols="12">
                    <v-alert text dense :type="senderChannelTipsType" v-show="true">{{ senderChannelTips }}</v-alert>
                  </v-col>
                </v-row>
                <v-data-table
                  :headers="senderChannelHeaders"
                  :items="senderChannels"
                  :loading="senderChannelLoading"
                  class="mt-4"
                >
                  <template v-slot:[`item.route_target`]="{ item }">
                    <span v-if="item.type === 'forward'">{{ item.forward_domain }}</span>
                    <span v-else>{{ item.ip }}</span>
                  </template>
                  <template v-slot:[`item.online`]="{ item }">
                    <v-chip :color="item.online ? 'green' : 'red'" dark small>
                      {{ item.online ? '在线' : '离线' }}
                    </v-chip>
                  </template>
                  <template v-slot:[`item.actions`]="{ item }">
                    <v-chip color="primary" dark @click="editSenderChannel(item)">
                      编辑
                    </v-chip>
                    <v-chip color="secondary" dark style="margin-left: 8px;" @click="deleteSenderChannel(item)">
                      删除
                    </v-chip>
                  </template>
                </v-data-table>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </v-tab-item>

      <v-tab-item v-if="isAdmin" value="system">
        <v-row>
          <v-col cols="12">
            <v-card>
              <v-card-title>
                SMTP MX（收件） 服务器状态
              </v-card-title>
              <v-card-text>
                <div v-if="smtpStatusLoading" style="color:#666;font-weight: bolder;">加载中...</div>
                <div v-else-if="is_running()" style="color:green;font-weight: bolder;">运行中 <v-btn x-small
                    @click="smtpStart()">重新启动</v-btn></div>
                <div v-else style="color:red;font-weight: bolder;">未运行 <v-btn x-small @click="smtpStart()">重新启动</v-btn>
                </div>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>

        <v-row>
          <v-col cols="12">
            <v-card>
              <v-card-title>
                <v-btn x-small @click="genAPK()">生成全局 API-KEY</v-btn>
              </v-card-title>
              <v-card-text>
                <div style="padding: 4px;">
                  <div>appId: <strong>{{ appId }}</strong> </div>
                  <div>appSecret: <strong>{{ appSecret }}</strong></div>
                </div>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>

        <v-row>
          <v-col cols="12">
            <v-card>
              <v-card-title>
                请填写私钥和证书信息 并保存&nbsp;&nbsp;<v-btn x-small @click="savePERM()">保存TLS key</v-btn>
              </v-card-title>
              <v-card-title>
                如果你不会填写，可以尝试 &nbsp;&nbsp; <v-btn x-small @click="genPERM()">自动生成全局 TLS key</v-btn>
              </v-card-title>
              <v-card-text>
                <v-textarea outlined id="idSetKEY" label="私钥文件" v-model="setKey" hint="私钥文件"
                  style="font-family:monospace;"></v-textarea>
                <v-textarea outlined id="idSetCert" label="证书信息" v-model="setCert" hint="证书信息"
                  style="font-family:monospace;"></v-textarea>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </v-tab-item>
    </v-tabs-items>
  </v-container>
</template>

<script>
import axios from 'axios';



export default {

  name: "vTyWorkSpace",

  data: () => ({
    logininfo: {},
    settingsTab: 'security',
    set_smtp_info: {},
    smtpStatusLoading: false,
    set_extra: {},
    appId: "",
    appSecret: "",
    setKey: "",
    setCert: "",
    setSaveMsg: "",
    f2a_loading: true,
    f2a_isBound: false,
    f2a_qrCodeUrl: '',
    f2a_secretKey: '',
    f2a_otpCode: '',
    f2a_tips: '',
    domainRegisterLoading: false,
    domainRegisterValue: false,
    domainRegisterTips: '',
    domainRegisterTipsType: 'info',
    groupMailboxes: [],
    groupMailboxLoading: false,
    groupMailboxTips: '',
    groupMailboxTipsType: 'info',
    groupMailboxForm: {
      local_part: '',
    },
    groupMailboxHeaders: [
      { text: '邮箱地址', value: 'mailbox' },
      { text: '授权数量', value: 'grant_count' },
      { text: '操作', value: 'actions', sortable: false },
    ],
    domainAccounts: [],
    domainAccountLoading: false,
    domainAccountTips: '',
    domainAccountTipsType: 'info',
    domainAccountForm: {
      id: null,
      local_part: '',
      tag: '',
      password: '',
    },
    domainAccountHeaders: [
      { text: '账号', value: 'uname' },
      { text: '显示名称', value: 'tag' },
      { text: '类型', value: 'account_type', sortable: false },
      { text: '额外授权邮箱', value: 'granted_mailboxes', sortable: false },
      { text: '创建时间', value: 'created_date' },
      { text: '操作', value: 'actions', sortable: false },
    ],
    mailboxGrantTips: '',
    mailboxGrantTipsType: 'info',
    selectedGrantAccountId: null,
    mailboxGrantForm: {
      mailbox: '',
    },
    senderChannels: [],
    senderChannelLoading: false,
    senderChannelTips: '',
    senderChannelTipsType: 'info',
    senderChannelForm: {
      id: null,
      domain: '',
      ip: '',
      forward_domain: '',
      type: 'inner',
    },
    senderChannelTypeItems: [
      { text: 'inner', value: 'inner' },
      { text: 'forward', value: 'forward' },
    ],
    senderChannelHeaders: [
      { text: '域名', value: 'domain' },
      { text: '目标', value: 'route_target' },
      { text: '渠道类型', value: 'type' },
      { text: '最近活跃时间', value: 'active_time' },
      { text: '状态', value: 'online' },
      { text: '操作', value: 'actions', sortable: false },
    ],

  }),
  methods: {

    logout: function () {
      axios.defaults.baseURL = process.env.VUE_APP_BASEURL;
      axios.post('/api/logout',).then(res => {
        console.log("logout RES", res);
        if (res.status != 200) {
          console.log("logout fail ", res);
          return;
        }

        window.location = '/';

      }
      )
        .catch(err => {
          console.log("verifyCode ERR", err);
        }
        );

    },


    verifyCode: function () {
      this.f2a_tips = '';
      axios.defaults.baseURL = process.env.VUE_APP_BASEURL;
      axios.post('/api/2fabind', { "otpcode": this.f2a_otpCode }).then(res => {
        console.log("Bind RES", res);
        if (res.status != 200) {
          console.log("failed bind 2fa")
        }
        if (res.data.code == 200) {
          this.f2a_isBound = true;
        } else {
          console.log("bind failed.");
          this.f2a_tips = '验证码错误，请重试'
        }
      }
      )
        .catch(err => {
          console.log("verifyCode ERR", err);
        }
        );

    },

    loadSetting: function () {
      axios.defaults.baseURL = process.env.VUE_APP_BASEURL;
      axios.get('/api/setFetch').then(res => {
        console.log("fecthSET", res);
        if (res.status == 200 && res.data.code == 200) {
          this.set_extra = res.data.data;
          this.appId = this.set_extra['appId'];
          this.appSecret = this.set_extra['appSecret'];
          this.setCert = this.set_extra['setCert'];
          this.setKey = this.set_extra['setKey'];
          console.log("sssss 1 =>", this.set_extra);
        }
      }).catch(err => {
        console.log("fecthSET ERR", err);
      }
      );

    },
    loadDomainRegisterSetting: function () {
      if (!this.isDomainUser) {
        return;
      }
      this.domainRegisterLoading = true;
      axios.defaults.baseURL = process.env.VUE_APP_BASEURL;
      axios.get('/api/domainRegisterSetting').then(res => {
        if (res.status == 200 && res.data.code == 200) {
          this.domainRegisterValue = Number((res.data.data || {}).allow_register || 0) === 1;
        } else {
          this.domainRegisterTips = res.data.message || '加载域名注册开关失败';
          this.domainRegisterTipsType = 'error';
        }
      }).catch(err => {
        this.domainRegisterTips = '加载域名注册开关失败';
        this.domainRegisterTipsType = 'error';
        console.log("loadDomainRegisterSetting ERR", err);
      }).finally(() => {
        this.domainRegisterLoading = false;
      });
    },
    updateDomainRegisterSetting: function () {
      if (!this.isDomainUser) {
        return;
      }
      this.domainRegisterLoading = true;
      axios.defaults.baseURL = process.env.VUE_APP_BASEURL;
      axios.post('/api/domainRegisterSetting', {
        allow_register: this.domainRegisterValue,
      }).then(res => {
        if (res.status == 200 && res.data.code == 200) {
          this.domainRegisterValue = Number((res.data.data || {}).allow_register || 0) === 1;
          this.domainRegisterTips = this.domainRegisterValue ? '已开启本域名注册' : '已关闭本域名注册';
          this.domainRegisterTipsType = 'success';
        } else {
          this.domainRegisterTips = res.data.message || '保存域名注册开关失败';
          this.domainRegisterTipsType = 'error';
        }
      }).catch(err => {
        this.domainRegisterTips = '保存域名注册开关失败';
        this.domainRegisterTipsType = 'error';
        console.log("updateDomainRegisterSetting ERR", err);
      }).finally(() => {
        this.domainRegisterLoading = false;
      });
    },
    normalizeDomainLocalPart: function (rawValue) {
      const domainName = (this.logininfo.domain_name || '').trim().toLowerCase();
      const value = (rawValue || '').trim().toLowerCase();
      if (!value) {
        return '';
      }
      if (value.indexOf('@') < 0) {
        return value;
      }
      const arr = value.split('@', 2);
      if (!arr[0]) {
        return null;
      }
      if (arr[1] && arr[1] !== domainName) {
        return null;
      }
      return arr[0];
    },
    buildDomainMailbox: function (rawValue, setTips) {
      const localPart = this.normalizeDomainLocalPart(rawValue);
      if (localPart === null) {
        setTips('邮箱域名固定为当前域名，请只填写名字部分', 'error');
        return '';
      }
      if (!localPart) {
        return '';
      }
      return `${localPart}@${this.logininfo.domain_name}`;
    },
    syncGroupMailboxGrantCount: function () {
      const grantCountMap = {};
      (this.domainAccounts || []).forEach(item => {
        (item.granted_mailboxes || []).forEach(mailboxValue => {
          const mailbox = (mailboxValue || '').toLowerCase();
          if (!mailbox) {
            return;
          }
          grantCountMap[mailbox] = (grantCountMap[mailbox] || 0) + 1;
        });
      });
      this.groupMailboxes = (this.groupMailboxes || []).map(item => ({
        ...item,
        grant_count: grantCountMap[(item.mailbox || '').toLowerCase()] || 0,
      }));
    },
    resetDomainAccountForm: function () {
      this.domainAccountForm = {
        id: null,
        local_part: '',
        tag: '',
        password: '',
      };
    },
    resetMailboxGrantForm: function () {
      this.mailboxGrantForm = {
        mailbox: '',
      };
    },
    manageDomainAccountMailboxes: function (item) {
      if (!item || Number(item.is_domain_admin || 0) === 1) {
        return;
      }
      this.selectedGrantAccountId = item.id;
      this.resetMailboxGrantForm();
      this.mailboxGrantTips = `正在管理 ${item.uname} 的邮箱授权`;
      this.mailboxGrantTipsType = 'info';
    },
    loadGroupMailboxes: function () {
      if (!this.canManageDomainAccounts) {
        return;
      }
      this.groupMailboxLoading = true;
      axios.defaults.baseURL = process.env.VUE_APP_BASEURL;
      axios.get('/api/groupMailboxes').then(res => {
        if (res.status == 200 && res.data.code == 200) {
          const data = res.data.data || {};
          this.groupMailboxes = (data.mailboxes || []).map(mailbox => ({ mailbox, grant_count: 0 }));
          this.syncGroupMailboxGrantCount();
        } else {
          this.groupMailboxTips = res.data.message || '加载代管邮箱失败';
          this.groupMailboxTipsType = 'error';
        }
      }).catch(err => {
        this.groupMailboxTips = '加载代管邮箱失败';
        this.groupMailboxTipsType = 'error';
        console.log("loadGroupMailboxes ERR", err);
      }).finally(() => {
        this.groupMailboxLoading = false;
      });
    },
    saveGroupMailbox: function () {
      const mailbox = this.buildDomainMailbox(this.groupMailboxForm.local_part, (message, type) => {
        this.groupMailboxTips = message;
        this.groupMailboxTipsType = type;
      });
      if (!mailbox) {
        if (!this.groupMailboxTips) {
          this.groupMailboxTips = '请填写邮箱名';
          this.groupMailboxTipsType = 'error';
        }
        this.groupMailboxTipsType = 'error';
        return;
      }
      axios.defaults.baseURL = process.env.VUE_APP_BASEURL;
      axios.post('/api/groupMailboxes', { mailbox }).then(res => {
        if (res.status == 200 && res.data.code == 200) {
          this.groupMailboxTips = '代管邮箱已添加';
          this.groupMailboxTipsType = 'success';
          this.groupMailboxForm.local_part = '';
          this.loadGroupMailboxes();
        } else {
          this.groupMailboxTips = res.data.message || '添加代管邮箱失败';
          this.groupMailboxTipsType = 'error';
        }
      }).catch(err => {
        this.groupMailboxTips = '添加代管邮箱失败';
        this.groupMailboxTipsType = 'error';
        console.log("saveGroupMailbox ERR", err);
      });
    },
    deleteGroupMailbox: function (item) {
      this.$confirm(() => {
        console.log("confirm delete group mailbox", item);
      }).then(() => {
        axios.defaults.baseURL = process.env.VUE_APP_BASEURL;
        axios.delete('/api/groupMailboxes', { params: { mailbox: item.mailbox } }).then(res => {
          if (res.status == 200 && res.data.code == 200) {
            this.groupMailboxTips = '代管邮箱已删除';
            this.groupMailboxTipsType = 'success';
            this.loadGroupMailboxes();
            this.loadDomainAccounts();
          } else {
            this.groupMailboxTips = res.data.message || '删除代管邮箱失败';
            this.groupMailboxTipsType = 'error';
          }
        }).catch(err => {
          this.groupMailboxTips = '删除代管邮箱失败';
          this.groupMailboxTipsType = 'error';
          console.log("deleteGroupMailbox ERR", err);
        });
      }).catch(err => {
        console.log("deleteGroupMailbox cancel", err);
      });
    },
    loadDomainAccounts: function () {
      if (!this.canManageDomainAccounts) {
        return;
      }
      this.domainAccountLoading = true;
      axios.defaults.baseURL = process.env.VUE_APP_BASEURL;
      axios.get('/api/domainAccounts').then(res => {
        if (res.status == 200 && res.data.code == 200) {
          this.domainAccounts = res.data.data || [];
          if (!this.selectedGrantAccount || Number(this.selectedGrantAccount.is_domain_admin || 0) === 1) {
            this.selectedGrantAccountId = null;
          }
          this.syncGroupMailboxGrantCount();
        } else {
          this.domainAccountTips = res.data.message || '加载账号失败';
          this.domainAccountTipsType = 'error';
        }
      }).catch(err => {
        this.domainAccountTips = '加载账号失败';
        this.domainAccountTipsType = 'error';
        console.log("loadDomainAccounts ERR", err);
      }).finally(() => {
        this.domainAccountLoading = false;
      });
    },
    editDomainAccount: function (item) {
      this.domainAccountForm = {
        id: item.id,
        local_part: (item.uname || '').split('@')[0] || '',
        tag: item.tag || '',
        password: '',
      };
      this.domainAccountTips = `正在编辑 ${item.uname}`;
      this.domainAccountTipsType = 'info';
    },
    saveDomainAccount: function () {
      const fullMailbox = this.buildDomainMailbox(this.domainAccountForm.local_part, (message, type) => {
        this.domainAccountTips = message;
        this.domainAccountTipsType = type;
      });
      const payload = {
        id: this.domainAccountForm.id,
        uname: fullMailbox,
        tag: (this.domainAccountForm.tag || '').trim(),
        password: (this.domainAccountForm.password || '').trim(),
      };
      if (!payload.id && !payload.uname) {
        if (!this.domainAccountTips) {
          this.domainAccountTips = '请填写邮箱名';
          this.domainAccountTipsType = 'error';
        }
        this.domainAccountTipsType = 'error';
        return;
      }
      if (!payload.id && !payload.password) {
        this.domainAccountTips = '请填写密码';
        this.domainAccountTipsType = 'error';
        return;
      }
      axios.defaults.baseURL = process.env.VUE_APP_BASEURL;
      axios.post('/api/domainAccounts', payload).then(res => {
        if (res.status == 200 && res.data.code == 200) {
          this.domainAccountTips = payload.id ? '账号已更新' : '账号已创建';
          this.domainAccountTipsType = 'success';
          this.resetDomainAccountForm();
          this.loadDomainAccounts();
        } else {
          this.domainAccountTips = res.data.message || '保存账号失败';
          this.domainAccountTipsType = 'error';
        }
      }).catch(err => {
        this.domainAccountTips = '保存账号失败';
        this.domainAccountTipsType = 'error';
        console.log("saveDomainAccount ERR", err);
      });
    },
    deleteDomainAccount: function (item) {
      this.$confirm(() => {
        console.log("confirm delete domain account", item);
      }).then(() => {
        axios.defaults.baseURL = process.env.VUE_APP_BASEURL;
        axios.delete('/api/domainAccounts', { params: { id: item.id } }).then(res => {
          if (res.status == 200 && res.data.code == 200) {
            this.domainAccountTips = '账号已删除';
            this.domainAccountTipsType = 'success';
            if (this.domainAccountForm.id == item.id) {
              this.resetDomainAccountForm();
            }
            if (Number(this.selectedGrantAccountId || 0) === Number(item.id)) {
              this.selectedGrantAccountId = null;
              this.resetMailboxGrantForm();
            }
            this.loadDomainAccounts();
          } else {
            this.domainAccountTips = res.data.message || '删除账号失败';
            this.domainAccountTipsType = 'error';
          }
        }).catch(err => {
          this.domainAccountTips = '删除账号失败';
          this.domainAccountTipsType = 'error';
          console.log("deleteDomainAccount ERR", err);
        });
      }).catch(err => {
        console.log("deleteDomainAccount cancel", err);
      });
    },
    promoteDomainAccount: function (item) {
      this.$confirm(() => {
        console.log("confirm promote domain account", item);
      }).then(() => {
        axios.defaults.baseURL = process.env.VUE_APP_BASEURL;
        axios.post('/api/domainAccounts/promote', { id: item.id }).then(res => {
          if (res.status == 200 && res.data.code == 200) {
            this.domainAccountTips = '账号已升级为主账号';
            this.domainAccountTipsType = 'success';
            if (Number(this.selectedGrantAccountId || 0) === Number(item.id)) {
              this.selectedGrantAccountId = null;
              this.resetMailboxGrantForm();
            }
            this.loadDomainAccounts();
            this.loadGroupMailboxes();
          } else {
            this.domainAccountTips = res.data.message || '升级主账号失败';
            this.domainAccountTipsType = 'error';
          }
        }).catch(err => {
          this.domainAccountTips = '升级主账号失败';
          this.domainAccountTipsType = 'error';
          console.log("promoteDomainAccount ERR", err);
        });
      }).catch(err => {
        console.log("promoteDomainAccount cancel", err);
      });
    },
    saveMailboxGrant: function () {
      const payload = {
        grantee_user_id: this.selectedGrantAccountId,
        mailbox: this.mailboxGrantForm.mailbox,
      };
      if (!payload.grantee_user_id) {
        this.mailboxGrantTips = '请选择普通账号';
        this.mailboxGrantTipsType = 'error';
        return;
      }
      if (!payload.mailbox) {
        this.mailboxGrantTips = '请选择邮箱资源';
        this.mailboxGrantTipsType = 'error';
        return;
      }
      axios.defaults.baseURL = process.env.VUE_APP_BASEURL;
      axios.post('/api/mailboxGrants', payload).then(res => {
        if (res.status == 200 && res.data.code == 200) {
          this.mailboxGrantTips = '邮箱授权已创建';
          this.mailboxGrantTipsType = 'success';
          this.resetMailboxGrantForm();
          this.loadDomainAccounts();
          this.loadGroupMailboxes();
        } else {
          this.mailboxGrantTips = res.data.message || '创建邮箱授权失败';
          this.mailboxGrantTipsType = 'error';
        }
      }).catch(err => {
        this.mailboxGrantTips = '创建邮箱授权失败';
        this.mailboxGrantTipsType = 'error';
        console.log("saveMailboxGrant ERR", err);
      });
    },
    deleteMailboxGrant: function (mailbox) {
      this.$confirm(() => {
        console.log("confirm delete mailbox grant", mailbox);
      }).then(() => {
        if (!this.selectedGrantAccountId) {
          this.mailboxGrantTips = '请选择普通账号';
          this.mailboxGrantTipsType = 'error';
          return;
        }
        axios.defaults.baseURL = process.env.VUE_APP_BASEURL;
        axios.delete('/api/mailboxGrants', {
          params: {
            grantee_user_id: this.selectedGrantAccountId,
            mailbox,
          }
        }).then(res => {
          if (res.status == 200 && res.data.code == 200) {
            this.mailboxGrantTips = '邮箱授权已取消';
            this.mailboxGrantTipsType = 'success';
            this.loadDomainAccounts();
            this.loadGroupMailboxes();
          } else {
            this.mailboxGrantTips = res.data.message || '取消邮箱授权失败';
            this.mailboxGrantTipsType = 'error';
          }
        }).catch(err => {
          this.mailboxGrantTips = '取消邮箱授权失败';
          this.mailboxGrantTipsType = 'error';
          console.log("deleteMailboxGrant ERR", err);
        });
      }).catch(err => {
        console.log("deleteMailboxGrant cancel", err);
      });
    },
    loadSenderChannels: function () {
      this.senderChannelLoading = true;
      axios.defaults.baseURL = process.env.VUE_APP_BASEURL;
      axios.get('/api/admin/senderChannels').then(res => {
        if (res.status == 200 && res.data.code == 200) {
          this.senderChannels = res.data.data || [];
        } else {
          this.senderChannelTips = res.data.message || '加载发信渠道失败';
          this.senderChannelTipsType = 'error';
        }
      }).catch(err => {
        this.senderChannelTips = '加载发信渠道失败';
        this.senderChannelTipsType = 'error';
        console.log("loadSenderChannels ERR", err);
      }).finally(() => {
        this.senderChannelLoading = false;
      });
    },
    resetSenderChannelForm: function () {
      this.senderChannelForm = {
        id: null,
        domain: '',
        ip: '',
        forward_domain: '',
        type: 'inner',
      };
    },
    editSenderChannel: function (item) {
      this.senderChannelForm = {
        id: item.id,
        domain: item.domain || '',
        ip: item.ip || '',
        forward_domain: item.forward_domain || '',
        type: item.type || 'inner',
      };
      const target = item.type === 'forward' ? item.forward_domain : item.ip;
      this.senderChannelTips = `正在编辑 ${item.domain} / ${target}`;
      this.senderChannelTipsType = 'info';
    },
    saveSenderChannel: function () {
      const payload = {
        id: this.senderChannelForm.id,
        domain: (this.senderChannelForm.domain || '').trim(),
        ip: (this.senderChannelForm.ip || '').trim(),
        forward_domain: (this.senderChannelForm.forward_domain || '').trim(),
        type: (this.senderChannelForm.type || '').trim() || 'inner',
      };
      if (!payload.domain) {
        this.senderChannelTips = '请填写发信域名';
        this.senderChannelTipsType = 'error';
        return;
      }
      if (payload.type === 'forward' && !payload.forward_domain) {
        this.senderChannelTips = 'forward 类型需要填写目标域名';
        this.senderChannelTipsType = 'error';
        return;
      }
      if (payload.type !== 'forward' && !payload.ip) {
        this.senderChannelTips = 'inner 类型需要填写节点 IP';
        this.senderChannelTipsType = 'error';
        return;
      }
      axios.defaults.baseURL = process.env.VUE_APP_BASEURL;
      axios.post('/api/admin/senderChannels', payload).then(res => {
        if (res.status == 200 && res.data.code == 200) {
          this.senderChannelTips = payload.id ? '发信渠道已更新' : '发信渠道已新增';
          this.senderChannelTipsType = 'success';
          this.resetSenderChannelForm();
          this.loadSenderChannels();
        } else {
          this.senderChannelTips = res.data.message || '保存发信渠道失败';
          this.senderChannelTipsType = 'error';
        }
      }).catch(err => {
        this.senderChannelTips = '保存发信渠道失败';
        this.senderChannelTipsType = 'error';
        console.log("saveSenderChannel ERR", err);
      });
    },
    deleteSenderChannel: function (item) {
      this.$confirm(() => {
        console.log("confirm delete senderChannel", item);
      }).then(() => {
        axios.defaults.baseURL = process.env.VUE_APP_BASEURL;
        axios.delete('/api/admin/senderChannels', { params: { id: item.id } }).then(res => {
          if (res.status == 200 && res.data.code == 200) {
            this.senderChannelTips = '发信渠道已删除';
            this.senderChannelTipsType = 'success';
            if (this.senderChannelForm.id == item.id) {
              this.resetSenderChannelForm();
            }
            this.loadSenderChannels();
          } else {
            this.senderChannelTips = res.data.message || '删除发信渠道失败';
            this.senderChannelTipsType = 'error';
          }
        }).catch(err => {
          this.senderChannelTips = '删除发信渠道失败';
          this.senderChannelTipsType = 'error';
          console.log("deleteSenderChannel ERR", err);
        });
      }).catch(err => {
        console.log("deleteSenderChannel cancel", err);
      });
    },
    is_running: function () {
      console.log("Info", this.set_smtp_info);
      if (this.set_smtp_info.start_ctime > 0)
        return true;
      return false
    },

    smtpStart: function () {
      this.smtpStatusLoading = true;
      axios.defaults.baseURL = process.env.VUE_APP_BASEURL;
      axios.get('/api/restartCSmtp').then(res => {
        console.log(res);
        if (res.status == 200 && res.data.code == 200) {
          this.set_smtp_info = res.data.data;
        } else {
          let dummy = 1;
          dummy;
        }
      }).catch(err => {
        console.log("play task", err);
      }).finally(() => {
        this.smtpStatusLoading = false;
      });
    },
    genAPK: function () {
      axios.defaults.baseURL = process.env.VUE_APP_BASEURL;
      axios.get('/api/getGlobalAppIDnSecret').then(res => {
        console.log(res);
        if (res.status == 200 && res.data.code == 200) {
          this.appId = res.data.data.appId;
          this.appSecret = res.data.data.appSecret;
        } else {
          let dummy = 1;
          dummy;
        }
      }).catch(err => {
        console.log("play task", err);
      });
    },
    genPERM: function () {
      axios.defaults.baseURL = process.env.VUE_APP_BASEURL;
      axios.get('/api/getGlobalPERM').then(res => {
        console.log("genPERM", res);
        if (res.status == 200 && res.data.code == 200) {
          this.setKey = res.data.data.setKey;
          this.setCert = res.data.data.setCert;
          this.setSaveMsg = "生成成功";
        } else {
          this.setSaveMsg = res.data.message;
          let dummy = 1;
          dummy;
        }
      }).catch(err => {
        console.log("play genPERM execept ", err);
      });
    },
    savePERM: function () {
      axios.defaults.baseURL = process.env.VUE_APP_BASEURL;
      let jsData = { "cert": this.setCert, "key": this.setKey };
      axios.post('/api/saveGlobalPERM', jsData).then(res => {
        console.log("save Cert ", res);
        if (res.status == 200 && res.data.code == 200) {
          this.setKey = res.data.data.setKey;
          this.setCert = res.data.data.setCert;
          this.setSaveMsg = "保存成功";
        } else {
          this.setSaveMsg = res.data.message;
          let dummy = 1;
          dummy;
        }
      }).catch(err => {
        console.log("play savePERM execept ", err);
      });
    },
    loadAdminData: function () {
      if (!this.isAdmin) {
        if (this.canManageDomainAccounts) {
          this.loadGroupMailboxes();
          this.loadDomainAccounts();
        }
        return;
      }
      this.loadSetting();
      this.loadSenderChannels();
      this.smtpStatusLoading = true;
      axios.defaults.baseURL = process.env.VUE_APP_BASEURL;
      axios.get('/api/getCSmtp').then(res => {
        console.log(res);
        if (res.status == 200 && res.data.code == 200) {
          this.set_smtp_info = res.data.data;
        }
      }).catch(err => {
        console.log("getCSmtp ERR", err);
      }).finally(() => {
        this.smtpStatusLoading = false;
      });
    }

  },
  mounted: function () {
  },
  computed: {
    settingTabs() {
      const tabs = [
        { text: '账户安全', value: 'security' },
      ];
      if (this.isDomainUser) {
        tabs.push({ text: '注册说明', value: 'register' });
      }
      if (this.canManageDomainAccounts) {
        tabs.push({ text: '邮箱成员', value: 'members' });
      }
      if (this.isAdmin) {
        tabs.push(
          { text: '发信渠道', value: 'channels' },
          { text: '系统管理', value: 'system' },
        );
      }
      return tabs;
    },
    isAdmin() {
      return Number(this.logininfo.role || 0) >= 50;
    },
    isDomainUser() {
      return !this.isAdmin && !!this.logininfo.domain_name;
    },
    canManageDomainAccounts() {
      return !this.isAdmin && Number(this.logininfo.is_domain_admin || 0) === 1 && !!this.logininfo.domain_name;
    },
    grantableAccounts() {
      return (this.domainAccounts || []).filter(item => Number(item.is_domain_admin || 0) !== 1);
    },
    selectedGrantAccount() {
      return (this.grantableAccounts || []).find(item => Number(item.id) === Number(this.selectedGrantAccountId || 0)) || null;
    },
    selectedGrantAccountMailboxes() {
      return this.selectedGrantAccount ? (this.selectedGrantAccount.granted_mailboxes || []) : [];
    },
    grantMailboxOptions() {
      const grantedMap = {};
      (this.selectedGrantAccountMailboxes || []).forEach(item => {
        grantedMap[(item || '').toLowerCase()] = true;
      });
      return (this.groupMailboxes || []).filter(item => !grantedMap[(item.mailbox || '').toLowerCase()]);
    },
    domainRegisterOpen: {
      get() {
        return this.domainRegisterValue === true;
      },
      set(value) {
        this.domainRegisterValue = value;
        return value;
      }
    }
  },
  async created() {
    axios.defaults.baseURL = process.env.VUE_APP_BASEURL;
    await axios.get('/api/profile').then(
      response => {
        console.log("profile ", response);
        if (response.status == 200 && response.data.code == 200) {
          console.log("ok");
          this.logininfo = response.data.data;
          if (response.data.data.flag_2fa == 0) {
            this.f2a_qrCodeUrl = '/api/2faqrcode';
            this.f2a_isBound = false;
            this.f2a_secretKey = response.data.data.enc_2fa;
          } else {
            this.f2a_isBound = true;
          }
          this.f2a_loading = false;
          this.loadAdminData();
        } else {
          window.location = '/login';
        }
      }
    )
      .catch(err => {
        window.location = '/login';
        console.log(err);
      });
  }
};

</script>
